import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { createApiKey, deleteApiKey } from '../../redux-components/actions/apiKeyActions';
import { customSwal } from '../CustomSwal';

export const keysForUser = (apiKeys, userId) => (apiKeys || []).filter(
  (key) => (key.user?._id || key.user) === userId
);

// One account's API keys, shown inside that account's panel: a key belongs to a user, so this is
// where it makes sense to read and manage them.
const AdminUserApiKeys = ({ user }) => {
  const dispatch = useDispatch();
  const { loading, apiKeys, error } = useSelector((state) => state.apiKeyList);

  const [name, setName] = useState('');
  const [allowedOrigins, setAllowedOrigins] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const keys = keysForUser(apiKeys, user._id);

  const handleCreate = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    dispatch(createApiKey({
      name: name.trim(),
      allowedOrigins: allowedOrigins.split(',').map((origin) => origin.trim()).filter(Boolean),
      assignedUserId: user._id,
    }));

    setName('');
    setAllowedOrigins('');
  };

  const handleDelete = (key) => {
    customSwal.fire({
      title: 'Are you sure?',
      text: `This will permanently delete the API key "${key.name}". Any site using it will stop working.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteApiKey(key._id));
      }
    });
  };

  const handleCopy = async (key) => {
    try {
      await navigator.clipboard.writeText(key.key);
      setCopiedId(key._id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      customSwal.fire({ icon: 'info', title: key.name, text: key.key });
    }
  };

  return (
    <div className="mt-5 border-t border-gray-700/70 pt-4">
      <h4 className="font-semibold">API keys</h4>
      <p className="mb-3 text-xs text-gray-400">
        Used by this account's website to read its console content through the public API.
      </p>

      {error && <p className="mb-3 text-sm text-red-300">{error}</p>}

      {loading && keys.length === 0 ? (
        <p className="text-sm text-gray-400">Loading keys…</p>
      ) : keys.length === 0 ? (
        <p className="text-sm text-gray-400">No keys yet.</p>
      ) : (
        <ul className="space-y-2">
          {keys.map((key) => (
            <li key={key._id} className="rounded-lg border border-gray-700/70 bg-gray-800/60 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {key.name}
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                        key.isActive ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'
                      }`}
                    >
                      {key.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                  <p className="mt-1 break-all font-mono text-xs text-gray-300 select-all">{key.key}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => handleCopy(key)}
                    className="rounded bg-gray-700 px-2 py-1 text-xs text-gray-200 hover:bg-gray-600"
                  >
                    <FontAwesomeIcon icon={faCopy} className="mr-1" />
                    {copiedId === key._id ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(key)}
                    title="Delete key"
                    aria-label="Delete key"
                    className="rounded bg-gray-700 px-2 py-1 text-xs text-gray-200 hover:bg-red-600 hover:text-white"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
                <div>
                  <dt className="inline">Origins: </dt>
                  <dd className="inline text-gray-300">
                    {key.allowedOrigins?.length ? key.allowedOrigins.join(', ') : 'any'}
                  </dd>
                </div>
                <div>
                  <dt className="inline">Requests: </dt>
                  <dd className="inline text-gray-300">{key.requestCount || 0}</dd>
                </div>
                <div>
                  <dt className="inline">Last used: </dt>
                  <dd className="inline text-gray-300">
                    {key.lastUsed ? moment(key.lastUsed).fromNow() : 'never'}
                  </dd>
                </div>
                <div>
                  <dt className="inline">Created: </dt>
                  <dd className="inline text-gray-300">
                    {key.createdAt ? moment(key.createdAt).format('ll') : '—'}
                  </dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleCreate} className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-[12rem] flex-1">
          <label htmlFor="api-key-name" className="block text-xs text-gray-400">Name</label>
          <input
            id="api-key-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. carollachance.com"
            className="w-full rounded-md bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="min-w-[12rem] flex-1">
          <label htmlFor="api-key-origins" className="block text-xs text-gray-400">
            Allowed origins (comma separated, blank for any)
          </label>
          <input
            id="api-key-origins"
            type="text"
            value={allowedOrigins}
            onChange={(e) => setAllowedOrigins(e.target.value)}
            placeholder="https://example.com"
            className="w-full rounded-md bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={!name.trim()}
          className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faPlus} />
          New key
        </button>
      </form>
    </div>
  );
};

export default AdminUserApiKeys;
